/* The small primitives of the console language: Frame (a ruled region, never a box), Rule, Grid (the one 12-column, 8 px
   grid, for screen and print), StatusLine (the one place an action says what happened: inline, in the flow, never floating)
   and ThemeToggle (light is the default; dark changes the site tokens only). Nothing here has a shadow, radius or overlay. */
import { createContext, useCallback, useContext, useEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react'
import { Moon, Sun, Check, Copy } from '@phosphor-icons/react'
import { useTheme } from '../../lib/hooks.tsx'
import { useLocation } from '../../lib/router.tsx'

export type Weight = 'hair' | 'heavy' | 'hatch'

/** A rule across the column: a hairline, a heavy ink line, or a hatched band. */
export function Rule({ weight = 'hair', className = '' }: { weight?: Weight; className?: string }) {
  return <hr className={`rule ${weight === 'heavy' ? 'is-heavy' : weight === 'hatch' ? 'is-hatch' : ''} ${className}`} />
}

/** A ruled region with a label in its head. The same props the old boxed region took, so a page keeps its structure. */
export function Frame({ title, sub, end, children, className = '', pad = false, id, answer }: { title?: ReactNode; sub?: ReactNode; end?: ReactNode; children: ReactNode; className?: string; pad?: boolean; id?: string; answer?: string }) {
  return (
    <section className={`frame ${className}`} id={id} data-answer={answer} aria-label={typeof title === 'string' ? title : undefined}>
      {title ? (
        <header className="frame-head">
          <h2>{title}</h2>
          {sub ? <div className="sub">{sub}</div> : null}
          {end ? <div className="end">{end}</div> : null}
        </header>
      ) : null}
      <div className={pad ? 'frame-body' : ''}>{children}</div>
    </section>
  )
}

/** A secondary tool or a long table behind a native disclosure: shut by default, one ruled line in the page, its count beside it. It opens in place (no route, no overlay). */
export function Disclose({ title, note, open = false, id, className = '', children }: { title: string; note?: ReactNode; open?: boolean; id?: string; className?: string; children: ReactNode }) {
  return (
    <details className={`disclose ${className}`} id={id} open={open || undefined}>
      <summary>
        <span className="disclose-title">{title}</span>
        {note ? <span className="disclose-note">{note}</span> : null}
      </summary>
      <div className="disclose-body">{children}</div>
    </details>
  )
}

/** Opens every disclosure the element sits in (or is), and reports whether it found the element. */
export function openTo(id: string): HTMLElement | null {
  const el = document.getElementById(id)
  if (!el) return null
  for (let n: HTMLElement | null = el; n; n = n.parentElement) if (n instanceof HTMLDetailsElement) n.open = true
  return el
}

/** The contents line of a long document: one line of anchors that jump to its sections. A section kept in a disclosure is opened by its link, and by an address that names it, and then jumped to. */
export function Contents({ items }: { items: readonly { id: string; label: string }[] }) {
  useEffect(() => {
    const go = () => {
      const id = decodeURIComponent(window.location.hash.slice(1))
      if (id && items.some((i) => i.id === id)) openTo(id)?.scrollIntoView({ block: 'start' })
    }
    go()
    window.addEventListener('hashchange', go)
    return () => window.removeEventListener('hashchange', go)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.map((i) => i.id).join('|')])
  return (
    <nav className="doc-contents" aria-label="Contents">
      <span className="doc-contents-tag">JUMP TO</span>
      {items.map((i) => (
        <a
          key={i.id}
          href={`#${i.id}`}
          onClick={(e) => {
            const el = openTo(i.id)
            if (!el) return
            e.preventDefault()
            window.history.replaceState(null, '', `#${i.id}`)
            el.scrollIntoView({ block: 'start' })
          }}
        >
          {i.label}
        </a>
      ))}
    </nav>
  )
}

type Span = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12

/** The 12-column grid. A child says how many columns it spans (`l`); the default is all 12. Below the 1024 px floor every child is a full row. */
export function Grid({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`grid12 ${className}`}>{children}</div>
}
export function Col({ l = 12, children, className = '' }: { l?: Span; children: ReactNode; className?: string }) {
  const style = { '--span': l } as CSSProperties
  return (
    <div className={className} style={style}>
      {children}
    </div>
  )
}

/** Whether the element scrolls inside itself, watched as the page and the element change size. */
export function useScrolls(ref: RefObject<HTMLElement | null>): boolean {
  const [scrolls, setScrolls] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const check = () => setScrolls(el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1)
    check()
    const watch = new ResizeObserver(check)
    watch.observe(el)
    if (el.firstElementChild) watch.observe(el.firstElementChild)
    return () => watch.disconnect()
  }, [ref])
  return scrolls
}

/** What a region that scrolls needs to be usable without a mouse: a tab stop, a role and a name. One that does not scroll is not a tab stop. */
const scrollable = (scrolls: boolean, label: string) => (scrolls ? { tabIndex: 0, role: 'region', 'aria-label': label } : {})

/** A table's wrapper: it scrolls inside itself when the table is wider or taller than it, and then it takes the keyboard (Tab to it, arrows to scroll) and says what it holds. */
export function TableWrap({ label, maxHeight, children }: { label: string; maxHeight?: number; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)
  const scrolls = useScrolls(ref)
  return (
    <div ref={ref} className="table-wrap" style={maxHeight ? { maxHeight } : undefined} {...scrollable(scrolls, label)}>
      {children}
    </div>
  )
}

export type Tone = 'info' | 'fail'
type Line = { id: number; text: string; tone: Tone }
type StatusApi = { line: Line | null; say: (text: string, tone?: Tone) => void; clear: () => void }
const StatusCtx = createContext<StatusApi>({ line: null, say: () => undefined, clear: () => undefined })

/** What the last action said. It holds until the next action speaks or the reader moves to another page. */
export function StatusProvider({ children }: { children: ReactNode }) {
  const [line, setLine] = useState<Line | null>(null)
  const next = useRef(1)
  const { path } = useLocation()
  const say = useCallback((text: string, tone: Tone = 'info') => setLine({ id: next.current++, text, tone }), [])
  const clear = useCallback(() => setLine(null), [])
  useEffect(() => setLine(null), [path])
  // A note says its piece and goes; a failure stays until the reader moves on.
  useEffect(() => {
    if (!line || line.tone === 'fail') return
    const t = window.setTimeout(() => setLine((l) => (l?.id === line.id ? null : l)), 6000)
    return () => window.clearTimeout(t)
  }, [line])
  return <StatusCtx.Provider value={{ line, say, clear }}>{children}</StatusCtx.Provider>
}
export const useStatus = () => useContext(StatusCtx).say

/** The status line: inline in the page, polite, and brought into view when it speaks. It is never positioned over content. */
export function StatusLine() {
  const { line } = useContext(StatusCtx)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!line || !el) return
    const r = el.getBoundingClientRect()
    if (r.top < 0 || r.bottom > window.innerHeight) el.scrollIntoView({ block: 'nearest' })
  }, [line])
  return (
    <div ref={ref} className={`status-line ${line ? (line.tone === 'fail' ? 'is-fail' : '') : 'is-idle'}`} role="status" aria-live="polite">
      {line ? (
        <>
          <span className="tag">{line.tone === 'fail' ? 'refused' : 'done'}</span>
          <span>{line.text}</span>
        </>
      ) : null}
    </div>
  )
}

/** Light is the default; this turns it dark and back. The choice is kept by the theme hook. */
export function ThemeToggle({ className = 'btn btn-sm btn-ghost' }: { className?: string }) {
  const theme = useTheme()
  const dark = theme.resolved === 'dark'
  return (
    <button className={className} onClick={theme.toggle} aria-label={`Switch to ${dark ? 'light' : 'dark'} mode`} aria-pressed={dark} style={{ justifyContent: 'flex-start' }}>
      {dark ? <Sun size={16} /> : <Moon size={16} />}
      {dark ? 'Light' : 'Dark'}
    </button>
  )
}

export function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [done, setDone] = useState(false)
  return (
    <button
      className="btn btn-sm btn-ghost"
      onClick={() => {
        void navigator.clipboard?.writeText(text).then(() => {
          setDone(true)
          window.setTimeout(() => setDone(false), 1600)
        })
      }}
      aria-label={done ? 'Copied to clipboard' : `${label || 'Copy'} to clipboard`}
    >
      {done ? <Check size={14} weight="bold" /> : <Copy size={14} />}
      {done ? 'Copied' : label}
    </button>
  )
}

/** A command or a snippet: mono, in a ruled frame. */
export function Code({ children, copy = true, label, wrap = false }: { children: string; copy?: boolean; label?: string; /** Wrap long lines instead of scrolling them sideways. */ wrap?: boolean }) {
  const pre = useRef<HTMLPreElement>(null)
  return (
    <div className="frame is-sunken">
      {label || copy ? (
        <div className="frame-head" style={{ borderBottom: '1px solid var(--line-strong)' }}>
          <span className="faint code-label">{label}</span>
          <span className="end">{copy ? <CopyButton text={children} /> : null}</span>
        </div>
      ) : null}
      <pre ref={pre} style={{ margin: 0, padding: 16, overflowX: wrap ? 'visible' : 'auto', ...(wrap ? { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' } : {}), fontSize: 13, lineHeight: 1.6, color: 'var(--ink-soft)' }} {...scrollable(useScrolls(pre), label || 'Code')}>
        <code>{children}</code>
      </pre>
    </div>
  )
}
